import type {
  ComponentOptions,
  InferPropTypes,
  PropsOptions,
  TrivialComponentInstance,
} from "./component/index.js";
import { getRef } from "./component/store";
import { getConfig } from "./config/index.js";
import { ON_PAGE_PRELOAD } from "./constant.js";
import { routeEmitter, userEmitter } from "./emitter/index.js";
import { getPathDetail, getTrigger } from "./navigator/index.js";
import type {
  ExtendedPageMethods,
  PageInstance,
  PageOptions,
  TrivialPageInstance,
} from "./page/index.js";

export type NavigatorType = "navigateTo" | "reLaunch" | "switchTab" | "redirectTo";

export type NavigatorOptionsType =
  | WechatMiniprogram.NavigateToOption
  | WechatMiniprogram.ReLaunchOption
  | WechatMiniprogram.RedirectToOption
  | WechatMiniprogram.SwitchTabOption;

export const go = getTrigger("navigateTo");
export const redirect = getTrigger("redirectTo");
export const switchTab = getTrigger("switchTab");
export const reLaunch = getTrigger("reLaunch");

const clickHandlerFactory = function (
  action: (pageName: string) => Promise<unknown>,
): (event: WechatMiniprogram.Touch) => Promise<void> {
  return async function touchHandler(
    this: TrivialPageInstance,
    event?: WechatMiniprogram.TouchEvent<
      WechatMiniprogram.IAnyObject,
      WechatMiniprogram.IAnyObject,
      // oxlint-disable-next-line typescript/no-unnecessary-type-arguments
      { before?: string; after?: string; url?: string }
    >,
  ): Promise<void> {
    if (event) {
      const { before, after, url } = event.currentTarget.dataset;

      // oxlint-disable-next-line typescript/strict-boolean-expressions
      if (this && before && typeof this[before] === "function")
        (this[before] as (event: WechatMiniprogram.Touch) => void)(event);

      if (url) {
        await action(url);

        // oxlint-disable-next-line typescript/strict-boolean-expressions
        if (this && after && typeof this[after] === "function")
          (this[after] as (event: WechatMiniprogram.Touch) => void)(event);
      }
    }
  };
};

const bindGo = clickHandlerFactory(go);
const bindRedirect = clickHandlerFactory(redirect);
const bindSwitch = clickHandlerFactory(switchTab);
const bindRelaunch = clickHandlerFactory(reLaunch);

/**
 * 返回，默认返回上一页
 *
 * @param delta - 后退层数
 * @returns Promise resolving to navigation result
 */
const back = (delta = 1): Promise<WechatMiniprogram.GeneralCallbackResult> => {
  const { home } = getConfig();

  return getCurrentPages().length <= delta && home ? reLaunch(home) : wx.navigateBack({ delta });
};

const bindBack = async function touchHandler(
  this: TrivialPageInstance,
  event?: WechatMiniprogram.TouchEvent<
    WechatMiniprogram.IAnyObject,
    WechatMiniprogram.IAnyObject,
    // oxlint-disable-next-line typescript/no-unnecessary-type-arguments
    { before?: string; after?: string; delta?: number | string }
  >,
): Promise<void> {
  if (event) {
    const { before, after, delta = 1 } = event.currentTarget.dataset;

    // oxlint-disable-next-line typescript/strict-boolean-expressions
    if (this && before && typeof this[before] === "function")
      (this[before] as (event: WechatMiniprogram.Touch) => void)(event);

    await Promise.resolve(back(Number(delta)));

    // oxlint-disable-next-line typescript/strict-boolean-expressions
    if (this && after && typeof this[after] === "function")
      (this[after] as (event: WechatMiniprogram.Touch) => void)(event);
  }
};

/**
 * 获得页面实例
 *
 * @returns 页面实例对象
 */
const getPage = <
  Data extends WechatMiniprogram.IAnyObject = WechatMiniprogram.IAnyObject,
  Custom extends WechatMiniprogram.IAnyObject = WechatMiniprogram.IAnyObject,
>(): PageInstance<Data, Custom> => [...getCurrentPages()].pop() as PageInstance<Data, Custom>;
/**
 * 预加载
 *
 * @param pageNameWithArg 需要预加载的地址
 */
const preload = (pageNameWithArg: string): void => {
  /** 页面名称 */
  const { path, query } = getPathDetail(pageNameWithArg);

  routeEmitter.emit(`${ON_PAGE_PRELOAD}:${path}`, query);
};

export const bind = function (
  this: TrivialComponentInstance,
  touchEvent: WechatMiniprogram.Touch<{
    id: number;
    event: string;
    args: unknown[];
  }>,
): void {
  const { args, event, id } = touchEvent.detail;

  switch (event) {
    // run private attach
    case "$attached": {
      const ref = getRef(id);

      if (!ref) break;

      const refName = ref.$refID;

      if (refName) this.$refs.set(refName, ref);

      ref.$attached(this);
      break;
    }
    default: {
      const method = this[event] as ((...args: unknown[]) => unknown) | undefined;

      if (method) method.apply(this, args);
    }
  }
};

/**
 * 挂载页面方法
 *
 * @param ctx 需要挂载页面的指针
 */
export function mount<
  Data extends WechatMiniprogram.IAnyObject,
  Custom extends WechatMiniprogram.IAnyObject,
>(ctx: PageOptions<Data, Custom> & Partial<ExtendedPageMethods<Data, Custom>>): void;

/**
 * 挂载组件方法
 *
 * @param ctx 需要挂载组件的指针
 */
export function mount<
  Data extends WechatMiniprogram.Component.DataOption,
  Property extends PropsOptions,
  Method extends WechatMiniprogram.Component.MethodOption,
  Behavior extends WechatMiniprogram.Component.BehaviorOption,
  InstanceProps extends WechatMiniprogram.IAnyObject = Record<never, never>,
  IsPage extends boolean = false,
>(
  ctx: ComponentOptions<Data, Property, Method, Behavior, InstanceProps, IsPage> &
    Partial<
      ExtendedPageMethods<
        Data & InferPropTypes<Property>,
        InstanceProps &
          Method &
          (IsPage extends true ? WechatMiniprogram.Page.ILifetime : Record<never, never>)
      >
    >,
): void;

export function mount(
  ctx: Partial<ExtendedPageMethods<WechatMiniprogram.IAnyObject, WechatMiniprogram.IAnyObject>> &
    WechatMiniprogram.IAnyObject,
): void {
  const config = getConfig();

  // oxlint-disable-next-line id-length
  ctx.$ = bind;

  // 实例引用集合
  ctx.$refs = new Map();

  // oxlint-disable-next-line typescript/unbound-method
  ctx.$on = userEmitter.on;
  // oxlint-disable-next-line typescript/unbound-method
  ctx.$off = userEmitter.off;
  // oxlint-disable-next-line typescript/unbound-method
  ctx.$emit = userEmitter.emit;
  // oxlint-disable-next-line typescript/unbound-method
  ctx.$emitAsync = userEmitter.emitAsync;

  // 路由方法
  ctx.$go = go;
  ctx.$redirect = redirect;
  ctx.$switch = switchTab;
  ctx.$reLaunch = reLaunch;
  ctx.$back = back;

  // 页面预加载
  ctx.$preload = preload;

  // 页面信息
  ctx.$currentPage = getPage;
  ctx.$getPath = config.getPath;

  // 点击跳转代理
  ctx.$bindGo = bindGo;
  ctx.$bindRedirect = bindRedirect;
  ctx.$bindSwitch = bindSwitch;
  ctx.$bindRelaunch = bindRelaunch;
  ctx.$bindBack = bindBack;
}
